import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ConversionBlock, PriceTag } from '@/pages/guias/components/GuiaComponents';
import { ServiceBreadcrumb, SchemaService } from '../components/ServicioComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';
import { SchemaPriceOffer } from '@/components/feature/SchemaOrg';

export default function InstalacionToldosMalaga() {
  return (
    <>
      <title>Instalación de toldos a medida en Málaga | Presupuesto gratis</title>
      <meta
        name="description"
        content="Instalación profesional de toldos a medida en Málaga y provincia. Toldos para terrazas, patios, áticos y comercios. Medición, asesoramiento y montaje incluido. Presupuesto gratis sin compromiso."
      />
      <meta
        name="keywords"
        content="instalacion toldos malaga, toldos a medida malaga, instalar toldo malaga, montaje toldos malaga, toldero malaga instalacion"
      />
      <link rel="canonical" href="https://toldos-malaga.es/instalacion-toldos-malaga" />
      <SchemaService
        name="Instalación de toldos a medida en Málaga"
        description="Servicio profesional de instalación de toldos a medida para viviendas particulares y negocios en Málaga y provincia. Incluye medición, asesoramiento personalizado, suministro y montaje. Presupuesto gratuito sin compromiso."
        priceRange="desde 350 €"
      />
      <SchemaPriceOffer price={550} name="Instalación de toldo articulado en Málaga" description="Instalación de toldo articulado de gran superficie con medición y montaje incluido." />
      <meta property="og:title" content="Instalación de toldos a medida en Málaga | Presupuesto gratis | Toldos Málaga" />
      <meta property="og:description" content="Instalación profesional de toldos a medida en Málaga y provincia. Toldos para terrazas, patios, áticos y comercios. Medición, asesoramiento y montaje incluido. Presupuesto gratis sin compromiso." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/instalacion-toldos-malaga" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Instalación de toldos a medida en Málaga | Toldos Málaga" />
      <meta name="twitter:description" content="Instalación profesional de toldos en Málaga. Medición, asesoramiento y montaje incluido. Presupuesto gratis." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />
      <Navbar />
      <main>
        <ServiceBreadcrumb currentTitle="Instalación de toldos a medida" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/001-servicio-instalacion-hero.webp"
              alt="Toldo a medida instalado profesionalmente en una terraza de Málaga"
              title="Instalación de toldos a medida en Málaga"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Servicio de instalación
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Instalación de toldos a medida en Málaga
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              Un toldo bien instalado transforma por completo una terraza, un patio o la fachada de un negocio. Pero no todos los espacios son iguales, y en Málaga menos aún: fachadas estrechas del centro histórico, áticos abiertos al levante, terrazas orientadas al sur con sol de 12 horas... Cada instalación tiene sus particularidades. Nosotros medimos, asesoramos y montamos —sin intermediarios, sin subcontratas—.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              No somos una tienda: somos instaladores
            </h2>

            <p>
              Hay una diferencia enorme entre comprar un toldo en un catálogo y que un toldero profesional te lo instale. El catálogo no ve tu fachada, no mide la orientación al sol, no sabe si el viento de levante te va a castigar la lona. Nosotros sí. Este es nuestro enfoque:
            </p>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-ruler-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Medición in situ, sin errores.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Nos desplazamos a tu vivienda o negocio, tomamos medidas exactas y evaluamos la fachada: tipo de pared, orientación, exposición al viento. Un toldo mal medido es un problema asegurado. Con nosotros, la medición va incluida en el presupuesto.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Asesoramiento sin presión comercial.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Te explicamos qué tipo de toldo se adapta mejor a tu espacio y a tu presupuesto. ¿Punto recto o articulado? ¿Acrílico o microperforado? ¿Manual o motorizado? Te damos nuestra opinión sincera, basada en años de experiencia en Málaga, no en la comisión del mes.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-settings-3-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Montaje profesional con garantía.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Fijación a fachada con tornillería adecuada al tipo de pared, nivelación milimétrica de brazos, tensado perfecto de la lona y configuración de finales de carrera en toldos eléctricos. Tu toldo queda instalado como tiene que quedar: para años.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Tipos de toldos que instalamos
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-2">Toldo de punto recto</h3>
                <p className="text-sm text-foreground-600">
                  El clásico. Sencillo, robusto y económico. Ideal para terrazas y ventanas de tamaño medio. Hasta 4 metros de proyección. Manual o motorizado.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-2">Toldo articulado</h3>
                <p className="text-sm text-foreground-600">
                  Brazos que se pliegan en dos o más secciones. Mayor proyección y mejor estética recogido. Perfecto para terrazas amplias y hostelería.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-2">Toldo vertical / estor</h3>
                <p className="text-sm text-foreground-600">
                  Protección lateral contra el sol de poniente, muy útil en Málaga para terrazas orientadas al oeste. También aporta privacidad.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-2">Toldo para hostelería</h3>
                <p className="text-sm text-foreground-600">
                  Estructuras reforzadas, lonas de alta resistencia y motorización con sensores de viento. Diseñados para el uso intensivo de bares y restaurantes.
                </p>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Por qué el clima de Málaga importa al elegir toldo?
            </h2>

            <p>
              Instalar un toldo en Málaga no es lo mismo que instalarlo en Burgos. Aquí el sol pega con una intensidad brutal —más de 3.000 horas de sol al año—, así que la lona tiene que ser acrílica de alta resistencia UV, no poliéster barato que se decolora en dos veranos. Y el viento de levante, que puede alcanzar rachas de 70 km/h, obliga a fijaciones extra-reforzadas y, si el toldo es eléctrico, a un sensor de viento que lo recoja automáticamente. Si instalas sin tener esto en cuenta, estás comprando un problema para la próxima tormenta de levante.
            </p>

            <div className="my-8">
              <ConversionBlock
                title="¿Quieres un toldo a medida en Málaga? Pide presupuesto"
                subtitle="Venimos a tu casa o negocio, medimos, te asesoramos y te damos un precio cerrado. Sin compromiso. Respuesta en 24 horas."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-8">
              <PriceTag price="desde 350 €" description="Toldo punto recto instalado" />
              <PriceTag price="desde 550 €" description="Toldo articulado instalado" />
              <p className="text-sm text-foreground-500">
                Precios orientativos para toldos estándar de 3-4 metros con lona acrílica e instalación incluida. Varía según dimensiones, tipo de fachada y motorización.
              </p>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}